import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import API from "../../services/api";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const PI_API = `${process.env.REACT_APP_API_URL}/api/pi`;

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fIN = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
const RAJ  = { fontFamily: "'Poppins',sans-serif" };
const MONO = { fontFamily: "'Poppins', sans-serif" };
const SANS = { fontFamily: "'Poppins',sans-serif" };

const BTN_BASE = "inline-flex items-center gap-[6px] rounded-[7px] font-semibold cursor-pointer transition-all duration-150 border-0";
const BTN_PRI  = "bg-[#7c6dfa] text-white hover:bg-[#9080ff] hover:-translate-y-px";
const BTN_OUT  = "bg-transparent border border-[#b8b8d4] text-[#1a1a2e] hover:border-[#7c6dfa]";
const BTN_AMBER= "bg-transparent border border-[rgba(240,192,64,0.4)] text-[#c89a00] hover:bg-[rgba(240,192,64,0.08)]";
const BTN_EDIT = "bg-transparent border border-[rgba(124,109,250,0.35)] text-[#7c6dfa] hover:bg-[rgba(124,109,250,0.08)]";
const INPUT_CLS =
  "bg-[#ffffff] border border-[#d0d0e8] rounded-[7px] px-[11px] py-[8px] " +
  "text-[#1a1a2e] text-[12px] outline-none placeholder-[#6c6c96] " +
  "transition-all focus:border-[#7c6dfa] focus:shadow-[0_0_0_2px_rgba(124,109,250,0.13)]";

// ─── STATUS CONFIG ────────────────────────────────────────────────────────────
const STATUS_MAP = {
  draft:     { bg: "rgba(124,109,250,0.1)", border: "rgba(124,109,250,0.3)", text: "#7c6dfa" },
  sent:      { bg: "rgba(64,192,240,0.1)",  border: "rgba(64,192,240,0.3)",  text: "#1a9ec0" },
  confirmed: { bg: "rgba(64,208,152,0.1)",  border: "rgba(64,208,152,0.3)",  text: "#40d098" },
  cancelled: { bg: "rgba(240,80,96,0.1)",   border: "rgba(240,80,96,0.3)",   text: "#f05060" },
};
const STATUSES = ["draft", "sent", "confirmed", "cancelled"];

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.draft;
  return (
    <span
      className="inline-block px-[9px] py-[2px] rounded-full text-[9.5px] font-semibold tracking-[1.2px] uppercase"
      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.text, ...MONO }}
    >
      {status}
    </span>
  );
};

// ─── SKELETON ROW ─────────────────────────────────────────────────────────────
const SkeletonRow = () => (
  <tr className="border-b border-[rgba(208,208,232,0.6)]">
    {[20, 120, 140, 80, 90, 80, 90, 90, 80].map((w, i) => (
      <td key={i} className="px-3 py-[13px]">
        <div
          className="h-3 rounded-[4px] bg-[#e8e8f2]"
          style={{ width: w, animation: `shimmer 1.6s ease-in-out ${i * 0.07}s infinite` }}
        />
      </td>
    ))}
  </tr>
);

// ─── TOAST ────────────────────────────────────────────────────────────────────
let _tid = 0;
function useToast() {
  const [list, setList] = useState([]);
  const toast = useCallback((msg, type = "ok") => {
    const id = ++_tid;
    setList((t) => [...t, { id, msg, type }]);
    setTimeout(() => setList((t) => t.filter((x) => x.id !== id)), 3500);
  }, []);
  return { list, toast };
}



// ─── PAGINATION ───────────────────────────────────────────────────────────────
function Pagination({ page, pages, total, limit, onPage }) {
  if (pages <= 1) return null;

  const getPages = () => {
    const arr = [];
    if (pages <= 7) {
      for (let i = 1; i <= pages; i++) arr.push(i);
      return arr;
    }
    arr.push(1);
    if (page > 3) arr.push("…");
    for (let i = Math.max(2, page - 1); i <= Math.min(pages - 1, page + 1); i++) arr.push(i);
    if (page < pages - 2) arr.push("…");
    arr.push(pages);
    return arr;
  };

  const btnCls = (active) =>
    `w-8 h-8 flex items-center justify-center rounded-[7px] text-[12px] font-semibold transition-all cursor-pointer border
    ${active
      ? "bg-[#7c6dfa] text-white border-[#7c6dfa] shadow-[0_2px_8px_rgba(124,109,250,0.3)]"
      : "bg-[#ffffff] text-[#4a4a75] border-[#d0d0e8] hover:border-[#7c6dfa] hover:text-[#7c6dfa]"}`;

  const from = (page - 1) * limit + 1;
  const to   = Math.min(page * limit, total);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-[#d0d0e8] bg-[rgba(240,240,252,0.6)]">
      <span className="text-[11px] text-[#4a4a75]" style={MONO}>
        Showing <span className="text-[#1a1a2e] font-semibold">{from}–{to}</span> of{" "}
        <span className="text-[#1a1a2e] font-semibold">{total}</span> PIs
      </span>

      <div className="flex items-center gap-[4px]">
        {/* Prev */}
        <button
          onClick={() => onPage(page - 1)}
          disabled={page === 1}
          className={`${btnCls(false)} disabled:opacity-30 disabled:cursor-not-allowed`}
          style={MONO}
        >‹</button>

        {getPages().map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} className="w-8 h-8 flex items-center justify-center text-[#4a4a75] text-[12px]" style={MONO}>…</span>
          ) : (
            <button key={p} onClick={() => onPage(p)} className={btnCls(p === page)} style={MONO}>{p}</button>
          )
        )}

        {/* Next */}
        <button
          onClick={() => onPage(page + 1)}
          disabled={page === pages}
          className={`${btnCls(false)} disabled:opacity-30 disabled:cursor-not-allowed`}
          style={MONO}
        >›</button>
      </div>
    </div>
  );
}

// ─── MAIN: PIList ─────────────────────────────────────────────────────────────
export default function PIList() {
  const navigate = useNavigate();
  const { list, toast } = useToast();

  const [pis, setPis]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage]       = useState(1);
  const [meta, setMeta]       = useState({ total: 0, pages: 1 });
  const LIMIT = 10;

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchPIs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: LIMIT,
        ...(search       && { search }),
        ...(statusFilter && { status: statusFilter }),
      });
      const res = await API.get(`${PI_API}?${params}`);
      const { data, total, pages } = res.data;
      setPis(data || []);
      setMeta({ total: total || 0, pages: pages || 1 });
    } catch (err) {
      toast(err.response?.data?.message || "Failed to load PIs", "err");
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]); // eslint-disable-line

  useEffect(() => { fetchPIs(); }, [fetchPIs]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset page when filter changes
  const handleStatusFilter = (val) => { setStatusFilter(val); setPage(1); };

  // ── Quick PDF (summary only, not full PI) ─────────────────────────────────
  const downloadSummaryPDF = (pi) => {
    try {
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(190, 30, 30);
      doc.text("RR INDUSTRIES", 105, 18, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text(`PI Ref No: ${pi.piRefNo}`, 15, 30);
      doc.text(`Date: ${fDate(pi.date || pi.createdAt)}`, 15, 37);
      doc.text(`Company: ${pi.companyName}`, 15, 44);
      doc.text(`GSTIN: ${pi.gstin || "—"}`, 15, 51);
      doc.text(`Status: ${(pi.status || "").toUpperCase()}`, 15, 58);

      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(15, 63, 195, 63);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      doc.text("Financial Summary", 15, 71);

      const rows = [
        ["Assessable Value",  `Rs. ${fIN(pi.assessableValue)}`],
        ["Loading Charge",    `Rs. ${fIN(pi.loadingCharge)}`],
        ["IGST 18%",          `Rs. ${fIN(pi.igst)}`],
        ["Grand Total",       `Rs. ${fIN(pi.grandTotal)}`],
        ["Total KGS",         `${Number(pi.totalKgs || 0).toFixed(2)} KGS`],
      ];

      autoTable(doc, {
        startY: 75,
        head: [["Description", "Amount"]],
        body: rows,
        theme: "grid",
        headStyles: { fillColor: [108, 99, 255], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 1: { halign: "right", fontStyle: "bold" } },
        margin: { left: 15, right: 15 },
      });

      doc.save(`PI-${pi.piRefNo.replace(/\//g, "-")}.pdf`);
      toast("Summary PDF downloaded ✓");
    } catch (err) {
      toast("PDF failed: " + err.message, "err");
    }
  };

  // ── Summary stats ─────────────────────────────────────────────────────────
  const totalGrand = pis.reduce((s, p) => s + (p.grandTotal || 0), 0);
  const totalKgs   = pis.reduce((s, p) => s + (p.totalKgs || 0), 0);

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #f4f4fa; color: #1a1a2e; }
        @keyframes shimmer { 0%,100%{opacity:.4} 50%{opacity:.8} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin    { to{transform:rotate(360deg)} }
        .anim-fade-up { animation: fadeUp .28s ease both; }
      `}</style>

      <div className="min-h-screen bg-[#f4f4fa]"
        style={{ backgroundImage: "radial-gradient(ellipse 70% 28% at 60% -8%, rgba(124,109,250,0.07), transparent 55%)" }}>
        <div className="max-w-[1540px] mx-auto px-4 py-6 md:px-6 md:py-8">

          {/* ── PAGE HEADER ── */}
          <div className="flex items-start justify-between flex-wrap gap-y-3 mb-8 anim-fade-up">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-1.5 h-6 rounded-full bg-gradient-to-b from-[#7c6dfa] to-[#40c0f0]" />
                <h1 className="text-[28px] font-bold text-[#1a1a2e] tracking-[1px]" style={RAJ}>
                  Proforma Invoices
                </h1>
              </div>
              <p className="text-[12px] text-[#4a4a75] pl-5" style={MONO}>
                {meta.total} PI{meta.total !== 1 ? "s" : ""} total
              </p>
            </div>
            <button
              onClick={() => navigate("/dashboard/pi/new")}
              className={`${BTN_BASE} ${BTN_PRI} px-5 py-[10px] text-[13px] shadow-[0_4px_18px_rgba(124,109,250,0.3)]`}
              style={RAJ}
            >
              <span className="text-[16px] leading-none">+</span> New PI
            </button>
          </div>

          {/* ── STATS STRIP ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 anim-fade-up" style={{ animationDelay: ".05s" }}>
            {[
              { label: "Total PIs",        val: meta.total,                cls: "text-[#7c6dfa]" },
              { label: "This Page — KGS",  val: totalKgs.toFixed(2),       cls: "text-[#40d098]" },
              { label: "This Page — Value",val: `₹${fIN(totalGrand)}`,    cls: "text-[#f0c040]" },
              { label: "Page",             val: `${page} / ${meta.pages}`, cls: "text-[#1a9ec0]" },
            ].map((s, i) => (
              <div key={i} className="bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl px-5 py-4 relative overflow-hidden shadow-sm">
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#b8b8d4] to-transparent" />
                <p className="text-[9px] tracking-[2px] uppercase text-[#4a4a75] mb-1" style={MONO}>{s.label}</p>
                <p className={`text-[22px] font-bold ${s.cls}`} style={RAJ}>{s.val}</p>
              </div>
            ))}
          </div>

          {/* ── FILTERS ── */}
          <div className="flex items-center gap-3 mb-4 flex-wrap anim-fade-up" style={{ animationDelay: ".08s" }}>
            {/* Search */}
            <div className="relative flex-1 min-w-[220px] max-w-[380px]">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a4a75] text-[14px]">⌕</span>
              <input
                className={`${INPUT_CLS} pl-8 w-full`}
                style={MONO}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search PI ref, company…"
              />
              {searchInput && (
                <button
                  onClick={() => { setSearchInput(""); setSearch(""); setPage(1); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4a4a75] hover:text-[#f05060] text-[14px] transition-colors"
                >✕</button>
              )}
            </div>

            {/* Status filter */}
            <select
              className={`${INPUT_CLS} min-w-[150px] cursor-pointer`}
              style={MONO}
              value={statusFilter}
              onChange={(e) => handleStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>

            {/* Active filter pills */}
            {(search || statusFilter) && (
              <div className="flex items-center gap-2">
                {search && (
                  <span className="text-[10px] bg-[rgba(124,109,250,0.1)] border border-[rgba(124,109,250,0.25)] text-[#7c6dfa] px-[9px] py-[3px] rounded-full" style={MONO}>
                    "{search}"
                  </span>
                )}
                {statusFilter && (
                  <span className="text-[10px] px-[9px] py-[3px] rounded-full uppercase" style={{
                    background: STATUS_MAP[statusFilter]?.bg,
                    border: `1px solid ${STATUS_MAP[statusFilter]?.border}`,
                    color: STATUS_MAP[statusFilter]?.text,
                    ...MONO,
                  }}>
                    {statusFilter}
                  </span>
                )}
                <button
                  onClick={() => { setSearch(""); setSearchInput(""); setStatusFilter(""); setPage(1); }}
                  className="text-[10px] text-[#f05060] hover:underline cursor-pointer"
                  style={MONO}
                >clear all</button>
              </div>
            )}

            <button
              onClick={fetchPIs}
              disabled={loading}
              className={`${BTN_BASE} ${BTN_OUT} px-4 py-[8px] text-[12px] ml-auto disabled:opacity-40`}
              style={RAJ}
            >
              {loading
                ? <span className="w-3 h-3 rounded-full border-2 border-[#b8b8d4] border-t-[#7c6dfa] inline-block" style={{ animation: "spin .7s linear infinite" }} />
                : "↺"}
              Refresh
            </button>
          </div>

          {/* ── TABLE ── */}
          <div
            className="relative h-full bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl  shadow-sm anim-fade-up"
            style={{ animationDelay: ".12s" }}
          >
            {/* Accent bar */}
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#7c6dfa] via-[#40c0f0] to-[#f0c040]" />

            <div className="">
              <table className="w-full border-collapse min-w-[960px]">
                <thead>
                  <tr className="bg-[#eeeef6] border-b border-[#d0d0e8]">
                    {[
                      { h: "#",             a: "center", w: "40px"  },
                      { h: "PI Ref No.",    a: "left"               },
                      { h: "Date",          a: "left",   w: "110px" },
                      { h: "Company",       a: "left"               },
                      { h: "Total KGS",     a: "right",  w: "100px" },
                      { h: "Grand Total",   a: "right",  w: "130px" },
                      { h: "Status",        a: "center", w: "130px" },
                      { h: "Created",       a: "left",   w: "110px" },
                      { h: "Actions",       a: "center", w: "140px" },
                    ].map((col) => (
                      <th
                        key={col.h}
                        className={`px-3 py-[11px] text-[9px] tracking-[1.2px] uppercase text-[#4a4a75] font-medium whitespace-nowrap text-${col.a}`}
                        style={{ ...MONO, width: col.w }}
                      >
                        {col.h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {loading
                    ? Array.from({ length: LIMIT }).map((_, i) => <SkeletonRow key={i} />)
                    : pis.length === 0
                    ? (
                      <tr>
                        <td colSpan={9} className="text-center py-16">
                          <div className="text-[40px] opacity-20 mb-3">📋</div>
                          <p className="text-[15px] font-semibold text-[#1a1a2e] opacity-30 mb-1" style={RAJ}>
                            {search || statusFilter ? "No PIs match your filters" : "No PIs yet"}
                          </p>
                          <p className="text-[12px] text-[#4a4a75]" style={MONO}>
                            {search || statusFilter ? "Try clearing filters" : "Create your first PI"}
                          </p>
                        </td>
                      </tr>
                    )
                    : pis.map((pi, i) => (
                      <tr
                        key={pi._id}
                        className="border-b border-[rgba(208,208,232,0.7)] transition-colors hover:bg-[rgba(124,109,250,0.03)] group"
                        style={{ animation: `fadeUp .22s ease ${i * 0.03}s both` }}
                      >
                        {/* # */}
                        <td className="px-3 py-[11px] text-center text-[11px] text-[#4a4a75]" style={MONO}>
                          {(page - 1) * LIMIT + i + 1}
                        </td>

                        {/* PI Ref */}
                        <td className="px-3 py-[11px]">
                          <button
                            onClick={() => navigate(`/pi/${pi._id}`)}
                            className="text-[12px] font-semibold text-[#7c6dfa] hover:underline cursor-pointer bg-transparent border-0"
                            style={MONO}
                          >
                            {pi.piRefNo}
                          </button>
                        </td>

                        {/* Date */}
                        <td className="px-3 py-[11px] text-[11px] text-[#4a4a75] whitespace-nowrap" style={MONO}>
                          {fDate(pi.date || pi.createdAt)}
                        </td>

                        {/* Company */}
                        <td className="px-3 py-[11px]">
                          <p className="text-[12px] font-semibold text-[#1a1a2e]" style={SANS}>{pi.companyName}</p>
                          {pi.gstin && (
                            <p className="text-[10px] text-[#4a4a75] mt-[1px]" style={MONO}>{pi.gstin}</p>
                          )}
                        </td>

                        {/* Total KGS */}
                        <td className="px-3 py-[11px] text-right">
                          <span className="text-[11px] font-medium text-[#40d098]" style={MONO}>
                            {Number(pi.totalKgs || 0).toFixed(2)}
                          </span>
                        </td>

                        {/* Grand Total */}
                        <td className="px-3 py-[11px] text-right">
                          <span className="text-[12px] font-bold text-[#f0c040]" style={MONO}>
                            ₹{fIN(pi.grandTotal)}
                          </span>
                        </td>

                        {/* Status badge */}
                        <td className="px-3 py-[11px] text-center">
                          <StatusBadge status={pi.status || "draft"} />
                        </td>

                        {/* Created */}
                        <td className="px-3 py-[11px] text-[10px] text-[#4a4a75] whitespace-nowrap" style={MONO}>
                          {fDate(pi.createdAt)}
                        </td>

                        {/* Actions */}
                        <td className="px-3 py-[11px] text-center">
                          <div className="flex items-center justify-center gap-2 opacity-50 group-hover:opacity-100 transition-opacity">
                            {/* Edit */}
                            <button
                              onClick={() => navigate(`/pi/${pi._id}`)}
                              className={`${BTN_BASE} ${BTN_EDIT} px-3 py-[5px] text-[11px]`}
                              style={RAJ}
                              title="Edit PI"
                            >
                              ✏ Edit
                            </button>

                            {/* Download summary PDF */}
                            {/* <button
                              onClick={() => downloadSummaryPDF(pi)}
                              className={`${BTN_BASE} ${BTN_AMBER} px-3 py-[5px] text-[11px]`}
                              style={RAJ}
                              title="Download summary PDF"
                            >
                              ↓ PDF
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>

                {/* Table footer totals */}
                {!loading && pis.length > 0 && (
                  <tfoot>
                    <tr style={{ borderTop: "1px solid #b8b8d4", background: "#eeeef6" }}>
                      <td colSpan={4} className="px-3 py-[9px] text-[11px] font-bold text-[#4a4a75] text-right tracking-[1px]" style={RAJ}>
                        PAGE TOTAL →
                      </td>
                      <td className="px-3 py-[9px] text-[11px] font-bold text-[#40d098] text-right" style={MONO}>
                        {totalKgs.toFixed(2)}
                      </td>
                      <td className="px-3 py-[9px] text-[12px] font-bold text-[#f0c040] text-right" style={MONO}>
                        ₹{fIN(totalGrand)}
                      </td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {/* ── PAGINATION ── */}
            <Pagination
              page={page}
              pages={meta.pages}
              total={meta.total}
              limit={LIMIT}
              onPage={setPage}
            />
          </div>

        </div>
      </div>

      {/* ── TOASTS ── */}
      <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[999] flex flex-col gap-[6px] items-center pointer-events-none">
        {list.map((t) => (
          <div
            key={t.id}
            className={[
              "bg-[#eeeef6] border rounded-[7px] px-5 py-[10px] text-[12px] whitespace-nowrap shadow-lg",
              "transition-all",
              t.type === "ok"  ? "border-[rgba(64,208,152,0.4)] text-[#40d098]" : "",
              t.type === "err" ? "border-[rgba(240,80,96,0.4)] text-[#f05060]"  : "",
              t.type !== "ok" && t.type !== "err" ? "border-[#b8b8d4] text-[#1a1a2e]" : "",
            ].join(" ")}
            style={MONO}
          >
            {t.msg}
          </div>
        ))}
      </div>
    </>
  );
}